import L from "leaflet";
import { BaseResponse } from "../../../base/response/BaseResponse";
import { MapGroup } from "../../base/map/BaseMapRender";
import { ClienteFincaLoteResponse } from "../../../modulos/terceros/response/ClienteFincaLoteResponse";

export type Tooltip<TResponse extends BaseResponse> = {
  key: string;
  label: string;
  get(response: TResponse | null, lote: ClienteFincaLoteResponse): string;
  onSelect?(): void;
};

export class TooltipRender<TResponse extends BaseResponse> {
  /** Campos disponibles en el selector. */
  private _tooltipFieldDefs: Tooltip<TResponse>[] = [];
  /** Campo actualmente seleccionado. */
  private _currentTooltipFieldDef: Tooltip<TResponse> | null = null;
  /** Capas de lote a las que se les aplica el tooltip. */
  private _loteLayers: MapGroup<TResponse>[] = [];
  /** Elemento `<select>` montado (o null antes de `init`/tras `dispose`). */
  private _select: HTMLSelectElement | null = null;

  /** Handler del `change` del select (nombrado para poder removerlo). */
  private _onChange = (): void => {
    if (!this._select) return;
    const value = parseInt(this._select.value);
    if (isNaN(value)) return;
    const selected = this._tooltipFieldDefs[value];
    if (!selected) return;
    this._currentTooltipFieldDef = selected;
    this._onTooltipFieldDefSelect(selected);
  };

  /** Crea el `<select>` con los campos y lo monta; deja el primero seleccionado. */
  init(
    parent: HTMLElement,
    tooltipFieldDefs: Tooltip<TResponse>[],
    loteLayers: MapGroup<TResponse>[],
  ): void {
    if (tooltipFieldDefs.length == 0) {
      return;
    }

    //

    this._tooltipFieldDefs = tooltipFieldDefs;
    this._currentTooltipFieldDef = this._tooltipFieldDefs[0];
    this._loteLayers = loteLayers;

    //

    // Solo se muestra el selector cuando hay más de un campo; con uno fijo, no.
    if (tooltipFieldDefs.length > 1) {
      const select: HTMLSelectElement = this._createSelect(tooltipFieldDefs);
      this._select = select;
      this._initSelectEvents(select);
      parent.appendChild(select);
    }
  }

  /** Quita el listener del select. */
  dispose(): void {
    if (this._select) L.DomEvent.off(this._select, "change", this._onChange);
    this._select = null;
  }

  /** Aplica el campo seleccionado a los tooltips (para el render inicial). */
  triggerTooltipFieldDef(): void {
    if (this._currentTooltipFieldDef) {
      this._onTooltipFieldDefSelect(this._currentTooltipFieldDef);
    }
  }

  /** Construye el `<select>` con una opción por campo. */
  private _createSelect(
    tooltipFieldDefs: Tooltip<TResponse>[],
  ): HTMLSelectElement {
    const select = L.DomUtil.create("select", "sx-select");

    let i = 0;
    for (const tooltipFieldDef of tooltipFieldDefs) {
      const opt = document.createElement("option");
      opt.value = i.toString();
      opt.textContent = tooltipFieldDef.label;
      select.appendChild(opt);
      i += 1;
    }

    return select;
  }

  /** Cablea el `change` del select para aplicar el campo elegido. */
  private _initSelectEvents(select: HTMLSelectElement) {
    L.DomEvent.on(select, "change", this._onChange);
    L.DomEvent.disableClickPropagation(select);
  }

  /** Aplica el campo a cada lote: crea el tooltip si falta y setea su contenido. */
  private _onTooltipFieldDefSelect(tooltipFieldDef: Tooltip<TResponse>) {
    this._loteLayers.forEach((loteLayer) => {
      loteLayer.layer.bindTooltip(
        tooltipFieldDef.get(loteLayer.response, loteLayer.lote),
        {
          className: "leaflet-lote-tooltip",
          direction: "center",
          permanent: true,
        },
      );
      if (tooltipFieldDef.onSelect) tooltipFieldDef.onSelect();
    });
  }
}
